<template>
  <div>
    <q-toolbar color="white" text-color="grey-9" class="top-page-toolbar">
      <q-toolbar-title class="q-body-2">
        <router-link class="text-grey-10 no-underline" :to="{ name: 'expenses-dashboard', params: { teamId: activeTeam.id } }">
          {{ $tc('expense', 2) }}
        </router-link>
        / <span class="text-weight-bold">{{ $t('captured_expenses') }}</span>
      </q-toolbar-title>

      <q-btn :label="$t('filter')" icon="filter_list" flat>
        <q-chip v-if="filterCount > 0" color="primary" floating>{{ filterCount }}</q-chip>
        <q-popover>
          <q-list separator link>
            <q-item>
              <q-item-side icon="calendar_today"/>
              <q-item-main>
                <q-datetime
                  :value="filter.InvoiceDateStart"
                  @change="val => { filter.InvoiceDateStart = val }"
                  type="date"
                  name="filterInvoiceDateStart"
                  class="q-mr-lg"
                  :placeholder="$t('expense_filter_invoice_date_from')"
                  :format="$t('date_format')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item>
              <q-item-side icon="calendar_today"/>
              <q-item-main>
                <q-datetime
                  :value="filter.InvoiceDateEnd"
                  @change="val => { filter.InvoiceDateEnd = val }"
                  type="date"
                  name="filterInvoiceDateEnd"
                  :placeholder="$t('expense_filter_invoice_date_to')"
                  :format="$t('date_format')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
            <q-item link tag="label">
              <q-item-side icon="payment"/>
              <q-item-main>
                <q-select
                  v-model="filter.isPaid"
                  :options="paidFilterOptions"
                  :placeholder="$t('expense_filter_payment')"
                  hide-underline
                  clearable
                />
              </q-item-main>
            </q-item>
          </q-list>
        </q-popover>
      </q-btn>
      <q-btn :label="$t('export')" flat size="md" @click.native="handleExport"/>
    </q-toolbar>
    <div class="layout-padding">
      <div v-if="!expensesLoaded" class="row justify-center">
        <q-spinner/>
      </div>

      <q-infinite-scroll v-else-if="expensesLoaded && expenses.length" :handler="handleFetchNextChunk">
        <div v-for="(expenses, key) in groupedExpenses" :key="key">
          <h3
            class="q-subheading text-bold text-blue-grey-4 q-mt-lg q-mb-sm"
            style="line-height: initial;"
          >
            {{ key }}
          </h3>
          <div class="row">
            <expense-list :expenses="expenses"/>
          </div>
        </div>
      </q-infinite-scroll>

      <div v-else class="flex column text-center">
        <img src="~assets/emptystates/items_empty.svg"/>
        <h6 class="q-my-md text-faded">{{ $t('no_expenses') }}</h6>
        <div>
          <q-btn @click="$router.push({ name: 'expenses-dashboard', params: { teamId: activeTeam.id } })" color="primary" class="q-mt-lg"
                 outline v-is-user>
            {{ $t('back_to_expense_dashboard') }}
          </q-btn>
        </div>
      </div>
    </div>
    <expense-export-modal ref="exportExpenseModal"></expense-export-modal>
  </div>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'
  import ExpenseExportModal from 'components/Expenses/ExpenseExportModal'
  import ExpenseList from 'components/Expenses/ExpenseList.vue'
  import { date } from 'quasar'
  import { groupBy } from 'lodash'
  import parseDate from 'date-fns/parse'
  import store from 'src/store'

  export default {
    components: {
      ExpenseExportModal,
      ExpenseList
    },
    data () {
      return {
        filter: {
          InvoiceDateStart: null,
          InvoiceDateEnd: null,
          isPaid: null
        }
      }
    },
    computed: {
      ...mapGetters(['activeTeam', 'expenses', 'expensesMeta', 'expensesLoaded']),
      filterForRequest () {
        let filter = []
        filter.push(['complete', '=', 'true'])

        if (this.filter.InvoiceDateStart !== null) {
          filter.push(['invoice_date', '>=', date.formatDate(parseDate(this.filter.InvoiceDateStart), 'YYYY-MM-DD')])
        }

        if (this.filter.InvoiceDateEnd !== null) {
          filter.push(['invoice_date', '<=', date.formatDate(parseDate(this.filter.InvoiceDateEnd), 'YYYY-MM-DD')])
        }

        if (this.filter.isPaid === false) {
          filter.push(['paid_at', '=', 'null'])
        }

        if (this.filter.isPaid === true) {
          filter.push(['paid_at', '!=', 'null'])
        }

        return filter
      },
      setFilters () {
        return Object.keys(this.filter).filter((key) => this.filter[key] !== null)
      },
      filterCount () {
        return this.setFilters.length
      },
      paidFilterOptions () {
        return [
          { label: this.$t('expense_filter_not_paid_only'), value: false },
          { label: this.$t('expense_filter_paid_only'), value: true }
        ]
      },
      groupedExpenses () {
        return groupBy(this.expenses, (expense) =>
          expense.accounting_period
            ? date.formatDate(parseDate(expense.accounting_period.date), 'MMMM')
            : null
        )
      }
    },
    methods: {
      ...mapActions(['listExpenses', 'fetchNextExpensesChunk']),
      updateRouteForFilter () {
        if (this.filterCount === 0) {
          const query = Object.assign({}, this.$route.query)
          delete query.f
          return this.$router.replace({ query })
        }

        const filter = JSON.stringify(this.filter)
        return this.$router.replace({ query: { ...this.$route.query, f: filter } })
      },
      parseFilterFromQuery () {
        if (Object.prototype.hasOwnProperty.call(this.$route.query, 'f')) {
          const { InvoiceDateStart = null, InvoiceDateEnd = null, isPaid = null } = JSON.parse(this.$route.query.f)

          this.filter.InvoiceDateStart = InvoiceDateStart
          this.filter.InvoiceDateEnd = InvoiceDateEnd
          this.filter.isPaid = isPaid
        }
      },
      handleRequest () {
        this.listExpenses({ pagination: {}, filter: this.filterForRequest })
      },
      handleFetchNextChunk (index, done) {
        this.fetchNextExpensesChunk({ done, pagination: {}, filter: this.filterForRequest })
      },
      handleExport () {
        this.$refs.exportExpenseModal.open()
      }
    },
    watch: {
      filter: {
        handler: function () {
          this.updateRouteForFilter()
          this.handleRequest()
        },
        deep: true
      }
    },
    beforeRouteEnter (to, from, next) {
      let filter = [['complete', '=', 'true']]

      if (Object.prototype.hasOwnProperty.call(to.query, 'f')) {
        const { InvoiceDateStart = null, InvoiceDateEnd = null, isPaid = null } = JSON.parse(to.query.f)

        if (InvoiceDateStart !== null) {
          filter.push(['invoice_date', '>=', date.formatDate(parseDate(this.filter.InvoiceDateStart), 'YYYY-MM-DD')])
        }

        if (InvoiceDateEnd !== null) {
          filter.push(['invoice_date', '<=', date.formatDate(parseDate(this.filter.InvoiceDateEnd), 'YYYY-MM-DD')])
        }

        if (isPaid === false) {
          filter.push(['paid_at', '=', 'null'])
        }

        if (isPaid === true) {
          filter.push(['paid_at', '!=', 'null'])
        }
      }

      store.dispatch('listExpenses', { pagination: {}, filter })
        .then(() => next((vm) => {
          vm.parseFilterFromQuery()
        }))
    }
  }
</script>
